import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
} from '@mui/material'
import CalculateIcon from '@mui/icons-material/Calculate'
import VisibilityIcon from '@mui/icons-material/Visibility'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  MetricCard,
  TrendChart,
  DateRangePicker,
  LocationComparisonChart,
} from '../../components/Performance'
import {
  useUndertakerAnalytics,
  useChainAnalytics,
  aggregateSummaries,
  splitSummariesForComparison,
  calculateTrend,
  cphDaysAgo,
} from '../../hooks/useAnalytics'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'

interface ClaimedEntity {
  id: string
  name: string
  type: 'undertaker' | 'chain'
  prisberegner_enabled?: boolean
}

interface ChainLocation {
  id: string
  name: string
  address?: string
}

interface RecentLead {
  id: string
  reference_number: string
  created_at: string
  contact_name: string | null
  pickup_address: string
  status: string
  price: number
}

export default function PerformancePrisberegner() {
  const { user, isAdmin } = useAuth()
  const [days, setDays] = useState(30)
  const [claimedEntities, setClaimedEntities] = useState<ClaimedEntity[]>([])
  const [selectedEntity, setSelectedEntity] = useState<ClaimedEntity | null>(null)
  const [chainLocations, setChainLocations] = useState<ChainLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('all')
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([])
  const [loading, setLoading] = useState(true)
  const [leadsLoading, setLeadsLoading] = useState(false)
  const [locationsLoading, setLocationsLoading] = useState(false)

  // Fetch entities with prisberegner (all if admin, only claimed if regular user)
  useEffect(() => {
    async function fetchEntities() {
      if (!user) return
      setLoading(true)
      
      try {
        const entities: ClaimedEntity[] = []
        
        if (isAdmin) {
          // Admin: Fetch ALL chains and standalone undertakers with prisberegner enabled
          const { data: undertakers } = await supabase
            .from('undertakers')
            .select('id, name, chain_id, prisberegner_enabled')
            .eq('prisberegner_enabled', true)
            .is('chain_id', null)
            .order('name')
          
          if (undertakers) {
            entities.push(...undertakers.map((u) => ({
              id: u.id,
              name: u.name,
              type: 'undertaker' as const,
              prisberegner_enabled: u.prisberegner_enabled,
            })))
          }
          
          const { data: chains } = await supabase
            .from('chains')
            .select('id, name, prisberegner_enabled')
            .eq('prisberegner_enabled', true)
            .order('name')
          
          if (chains) {
            entities.push(...chains.map((c) => ({
              id: c.id,
              name: c.name,
              type: 'chain' as const,
              prisberegner_enabled: c.prisberegner_enabled,
            })))
          }
        } else {
          // Regular user: Fetch only claimed standalone undertakers and chains with prisberegner
          const { data: undertakers } = await supabase
            .from('undertakers')
            .select('id, name, chain_id, prisberegner_enabled')
            .eq('claimed_by', user.id)
            .eq('claim_approved', true)
            .is('chain_id', null)
            .order('name')
          
          if (undertakers) {
            entities.push(...undertakers.map((u) => ({
              id: u.id,
              name: u.name,
              type: 'undertaker' as const,
              prisberegner_enabled: u.prisberegner_enabled,
            })))
          }
          
          const { data: chains } = await supabase
            .from('chains')
            .select('id, name, prisberegner_enabled')
            .eq('claimed_by', user.id)
            .eq('claim_approved', true)
            .order('name')
          
          if (chains) {
            entities.push(...chains.map((c) => ({
              id: c.id,
              name: c.name,
              type: 'chain' as const,
              prisberegner_enabled: c.prisberegner_enabled,
            })))
          }
        }
        
        setClaimedEntities(entities)
        const enabledEntities = entities.filter((e) => e.prisberegner_enabled)
        if (enabledEntities.length > 0 && !selectedEntity) {
          setSelectedEntity(enabledEntities[0])
        }
      } catch (error) {
        console.error('Error fetching entities:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchEntities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin])

  // Fetch chain locations when chain is selected
  useEffect(() => {
    async function fetchChainLocations() {
      if (selectedEntity?.type !== 'chain') {
        setChainLocations([])
        setSelectedLocation('all')
        return
      }
      
      setLocationsLoading(true)
      try {
        const { data: undertakers } = await supabase
          .from('undertakers')
          .select('id, name, address, prisberegner_enabled')
          .eq('chain_id', selectedEntity.id)
          .eq('prisberegner_enabled', true)
          .order('name')
        
        setChainLocations(undertakers || [])
        setSelectedLocation('all')
      } catch (error) {
        console.error('Error fetching chain locations:', error)
        setChainLocations([])
      } finally {
        setLocationsLoading(false)
      }
    }
    
    fetchChainLocations()
  }, [selectedEntity])

  // Fetch recent leads for selected entity/location
  useEffect(() => {
    async function fetchRecentLeads() {
      if (!selectedEntity) return
      setLeadsLoading(true)
      
      try {
        const query = supabase
          .from('prisberegner_offers')
          .select(`
            id,
            price,
            status,
            created_at,
            request:prisberegner_requests!inner(
              reference_number,
              contact_name,
              pickup_address,
              status
            )
          `)
          .order('created_at', { ascending: false })
          .limit(10)
        
        if (selectedEntity.type === 'undertaker') {
          query.eq('undertaker_id', selectedEntity.id)
        } else if (selectedEntity.type === 'chain') {
          if (selectedLocation === 'all') {
            query.eq('chain_id', selectedEntity.id)
          } else {
            query.eq('undertaker_id', selectedLocation)
          }
        }
        
        const { data, error } = await query
        
        if (error) throw error
        
        const leads = (data || []).map((offer: any) => ({
          id: offer.id,
          reference_number: offer.request?.reference_number || '-',
          created_at: offer.created_at,
          contact_name: offer.request?.contact_name,
          pickup_address: offer.request?.pickup_address || '-',
          status: offer.status,
          price: offer.price,
        }))
        
        setRecentLeads(leads)
      } catch (error) {
        console.error('Error fetching recent leads:', error)
        setRecentLeads([])
      } finally {
        setLeadsLoading(false)
      }
    }
    
    fetchRecentLeads()
  }, [selectedEntity, selectedLocation])

  // Fetch analytics based on selected entity and location
  const undertakerAnalytics = useUndertakerAnalytics(
    selectedEntity?.type === 'undertaker' 
      ? selectedEntity.id 
      : (selectedEntity?.type === 'chain' && selectedLocation !== 'all')
      ? selectedLocation
      : null,
    days
  )
  const chainAnalytics = useChainAnalytics(
    selectedEntity?.type === 'chain' && selectedLocation === 'all' 
      ? selectedEntity.id 
      : null,
    days
  )
  
  const analyticsData = selectedEntity?.type === 'undertaker' || selectedLocation !== 'all'
    ? undertakerAnalytics 
    : chainAnalytics
  
  const summaries = analyticsData.data || []
  const metrics = aggregateSummaries(summaries)
  const { current, previous } = splitSummariesForComparison(summaries, days)
  
  // Fetch individual location data for comparison
  const [locationComparisons, setLocationComparisons] = useState<any[]>([])
  useEffect(() => {
    async function fetchLocationComparisons() {
      if (selectedEntity?.type !== 'chain' || chainLocations.length === 0) {
        setLocationComparisons([])
        return
      }
      
      try {
        // One batched query instead of one per location
        const { data } = await supabase
          .from('analytics_daily_summary')
          .select('*')
          .in('undertaker_id', chainLocations.map((l) => l.id))
          .gte('summary_date', cphDaysAgo(days))

        const locationData = chainLocations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          ...aggregateSummaries((data || []).filter((r) => r.undertaker_id === loc.id)),
        }))

        setLocationComparisons(locationData)
      } catch (error) {
        console.error('Error fetching location comparisons:', error)
        setLocationComparisons([])
      }
    }
    
    if (selectedEntity?.type === 'chain' && selectedLocation === 'all') {
      fetchLocationComparisons()
    }
  }, [selectedEntity, chainLocations, days, selectedLocation])

  // Calculate conversion rate
  const conversionRate = metrics.total_prisberegner_impressions > 0
    ? (metrics.total_prisberegner_selections / metrics.total_prisberegner_impressions) * 100
    : 0

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'selected':
        return 'success'
      case 'pending':
        return 'warning'
      case 'expired':
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'selected':
        return 'Valgt'
      case 'pending':
        return 'Afventer'
      case 'expired':
        return 'Udløbet'
      case 'rejected':
        return 'Afvist'
      default:
        return status
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const enabledEntities = claimedEntities.filter((e) => e.prisberegner_enabled)
  
  if (enabledEntities.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography fontWeight={600} gutterBottom>
          Prisberegner ikke aktiveret
        </Typography>
        <Typography variant="body2">
          {isAdmin
            ? 'Ingen profiler har Prisberegner aktiveret endnu.'
            : 'Du har ingen profiler med Prisberegner aktiveret. Kontakt os for at komme i gang med Prisberegner.'
          }
        </Typography>
      </Alert>
    )
  }

  // Separate undertakers and chains for grouped dropdown
  const undertakers = enabledEntities.filter((e) => e.type === 'undertaker')
  const chains = enabledEntities.filter((e) => e.type === 'chain')

  return (
    <Box>
      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          {/* First dropdown: Select entity */}
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>
              {isAdmin ? 'Vælg profil' : 'Vælg din profil'}
            </InputLabel>
            <Select
              value={selectedEntity?.id || ''}
              label={isAdmin ? 'Vælg profil' : 'Vælg din profil'}
              onChange={(e) => {
                const entity = enabledEntities.find((en) => en.id === e.target.value)
                setSelectedEntity(entity || null)
              }}
            >
              {undertakers.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem' }}>
                  BEDEMÆND
                </MenuItem>
              )}
              {undertakers.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name}
                </MenuItem>
              ))}
              {chains.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.75rem', mt: 1 }}>
                  KÆDER
                </MenuItem>
              )}
              {chains.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Second dropdown: Select location (only for chains) */}
          {selectedEntity?.type === 'chain' && chainLocations.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Vælg lokation</InputLabel>
              <Select
                value={selectedLocation}
                label="Vælg lokation"
                onChange={(e) => setSelectedLocation(e.target.value)}
                disabled={locationsLoading}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={600}>📊 Alle lokationer (overblik)</Typography>
                  </Box>
                </MenuItem>
                {chainLocations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {loc.name}
                      </Typography>
                      {loc.address && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {loc.address}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <DateRangePicker value={days} onChange={setDays} />
      </Box>

      {/* Current Profile */}
      {selectedEntity && (
        <Card sx={{ mb: 3, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CalculateIcon sx={{ color: 'warning.main' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedEntity.name}
                  </Typography>
                  <Chip
                    label="Prisberegner"
                    size="small"
                    sx={{
                      backgroundColor: alpha('#d97706', 0.1),
                      color: '#d97706',
                      fontWeight: 500,
                    }}
                  />
                </Box>
                {selectedEntity.type === 'chain' && selectedLocation !== 'all' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={chainLocations.find(l => l.id === selectedLocation)?.name || 'Lokation'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'warning.main',
                        color: 'warning.main',
                        fontWeight: 500,
                      }}
                    />
                    {chainLocations.find(l => l.id === selectedLocation)?.address && (
                      <Typography variant="body2" color="text.secondary">
                        {chainLocations.find(l => l.id === selectedLocation)?.address}
                      </Typography>
                    )}
                  </Box>
                )}
                {selectedEntity.type === 'chain' && selectedLocation === 'all' && (
                  <Chip
                    label={`${chainLocations.length} lokationer`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'warning.main',
                      color: 'warning.main',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {analyticsData.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metric Cards - Funnel */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
              <MetricCard
                title="Visninger i Prisberegner"
                value={metrics.total_prisberegner_impressions}
                trend={calculateTrend(current.total_prisberegner_impressions, previous.total_prisberegner_impressions)}
                trendLabel="vs. forrige periode"
                icon={<VisibilityIcon sx={{ fontSize: 20 }} />}
                color="primary"
              />
            </Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
              <MetricCard
                title="Valgt af brugere"
                value={metrics.total_prisberegner_selections}
                trend={calculateTrend(current.total_prisberegner_selections, previous.total_prisberegner_selections)}
                trendLabel="vs. forrige periode"
                icon={<TouchAppIcon sx={{ fontSize: 20 }} />}
                color="info"
              />
            </Box>
            {/* prisberegner_leads card removed — aggregation never writes that
                field. Real leads are in the "Seneste leads" table below. */}
            <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
              <MetricCard
                title="Konverteringsrate"
                value={conversionRate}
                format="percentage"
                subtitle="Fra visning til valg"
                icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                color="warning"
              />
            </Box>
          </Box>

          {/* Location Comparison Chart (only for chains with "all" selected) */}
          {selectedEntity?.type === 'chain' && selectedLocation === 'all' && locationComparisons.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <LocationComparisonChart 
                  locations={locationComparisons}
                  metrics={[
                    { key: 'total_prisberegner_impressions', label: 'Visninger', color: '#5c4b99' },
                    { key: 'total_prisberegner_selections', label: 'Valgt', color: '#0284c7' },
                  ]}
                  title="Sammenligning af Prisberegner-performance mellem lokationer"
                />
              </CardContent>
            </Card>
          )}

          {/* Trend Chart */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <TrendChart
                data={summaries}
                metrics={[
                  { key: 'prisberegner_impressions', label: 'Visninger', color: '#5c4b99' },
                  { key: 'prisberegner_selections', label: 'Valgt', color: '#0284c7' },
                ]}
                title="Prisberegner aktivitet"
                height={350}
              />
            </CardContent>
          </Card>

          {/* Recent Leads Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Seneste leads
              </Typography>
              {leadsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : recentLeads.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    Ingen leads endnu. Leads vises her når brugere vælger dit tilbud i Prisberegneren.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Dato</TableCell>
                        <TableCell>Kontakt</TableCell>
                        <TableCell>Afhentning</TableCell>
                        <TableCell align="right">Pris</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentLeads.map((lead) => (
                        <TableRow key={lead.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {lead.reference_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(lead.created_at).toLocaleDateString('da-DK', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </TableCell>
                          <TableCell>
                            {lead.contact_name || <Typography color="text.secondary" variant="body2">-</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {lead.pickup_address}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {lead.price.toLocaleString('da-DK')} kr.
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getStatusLabel(lead.status)}
                              color={getStatusColor(lead.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}
